import dayjs from "dayjs";

/**
 * Calendar days from today until the statute date (start-of-day).
 * Used with {@link getStatuteDaysBadgeVariant} on approaching-statutes / dashboard cards.
 */
export function getCalendarDaysUntilStatute(
  statuteDate?: string | null,
): number | null {
  if (!statuteDate) {
    return null;
  }
  const parsed = dayjs(statuteDate);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.startOf("day").diff(dayjs().startOf("day"), "day");
}

/**
 * Tailwind text classes for a SOL date. Urgency bands match
 * {@link getStatuteDaysBadgeVariant} (dashboard + approaching-statutes reports).
 * Overdue and ≤30 days both use theme destructive red — a single red, not mixed with raw `red-*` utilities.
 */
export function getStatuteDateDisplayClassName(
  statuteDate?: string | null,
): string {
  const daysRemaining = getCalendarDaysUntilStatute(statuteDate);
  if (daysRemaining === null) {
    return "text-foreground";
  }
  if (daysRemaining <= 30) {
    return "text-destructive";
  }
  if (daysRemaining <= 60) {
    return "text-amber-800 dark:text-amber-300";
  }
  return "text-green-800 dark:text-green-300";
}

/** Badge variant for “days remaining” cells; one red (`destructive`) for overdue and ≤30 days. */
export function getStatuteDaysBadgeVariant(
  daysRemaining: number | null,
): "secondary" | "destructive" | "amber" | "green" {
  if (daysRemaining === null) {
    return "secondary";
  }
  if (daysRemaining <= 30) {
    return "destructive";
  }
  if (daysRemaining <= 60) {
    return "amber";
  }
  return "green";
}
